'use client';

import React, { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const textAreaClasses = cva(
  'w-full resize-vertical transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed hover:border-opacity-70',
  {
    variants: {
      variant: {
        default: 'focus:ring-blue-500',
        error: 'border-red-500 focus:ring-red-500',
        success: 'border-green-500 focus:ring-green-500',
      },
      size: {
        small: 'text-sm px-2 py-1 min-h-[60px]',
        medium: 'text-base px-3 py-2 min-h-[80px]',
        large: 'text-lg px-4 py-3 min-h-[120px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'medium',
    },
  }
);

const TextArea = forwardRef(({
  // Required parameters with defaults
  placeholder = "Pesan Anda",
  text_font_size = "14",
  text_font_family = "Plus Jakarta Sans",
  text_font_weight = "400",
  text_line_height = "18px",
  text_text_align = "left",
  text_color = "#9badbc",
  fill_background_color = "#ffffff",
  border_border = "1 solid #002d8114",
  border_border_radius = "10px",
  
  // Optional parameters (no defaults)
  layout_width,
  padding,
  position,
  
  // Standard React props
  variant,
  size,
  disabled = false,
  className,
  value,
  onChange,
  onFocus,
  onBlur,
  rows = 4,
  cols,
  maxLength,
  ...props
}, ref) => {
  // Safe validation for optional parameters
  const hasValidWidth = layout_width && typeof layout_width === 'string' && layout_width?.trim() !== '';
  const hasValidPadding = padding && typeof padding === 'string' && padding?.trim() !== '';
  const hasValidPosition = position && typeof position === 'string' && position?.trim() !== '';

  // Build optional Tailwind classes
  const optionalClasses = [
    hasValidWidth ? `w-[${layout_width}]` : '',
    hasValidPadding ? `p-[${padding}]` : '',
    hasValidPosition ? position : '',
  ]?.filter(Boolean)?.join(' ');

  // Parse border from border_border parameter
  const parseBorder = (borderString) => {
    if (!borderString) return {};
    
    // Parse "1 solid #002d8114" format
    const parts = borderString?.split(' ');
    const width = parts?.[0] || '1';
    const style = parts?.[1] || 'solid';
    const color = parts?.[2] || '#002d8114';
    
    return {
      borderWidth: `${width}px`,
      borderStyle: style,
      borderColor: color,
    };
  };

  // Build inline styles for required parameters
  const textAreaStyles = {
    fontSize: text_font_size ? `${text_font_size}px` : '14px',
    fontFamily: text_font_family || 'Plus Jakarta Sans',
    fontWeight: text_font_weight || '400',
    lineHeight: text_line_height || '18px',
    textAlign: text_text_align || 'left',
    color: text_color || '#9badbc',
    backgroundColor: fill_background_color || '#ffffff',
    borderRadius: border_border_radius || '10px',
    ...parseBorder(border_border),
  };

  return (
    <textarea data-component-id="src\components\ui\TextArea.jsx:104:4" data-component-path="src\components\ui\TextArea.jsx" data-component-line="104" data-component-end-line="124" data-component-file="TextArea.jsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      ref={ref}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
      rows={rows}
      cols={cols}
      maxLength={maxLength}
      style={textAreaStyles}
      className={twMerge(
        textAreaClasses({ variant, size }),
        'px-3 py-2 sm:px-4 sm:py-3 md:px-5 md:py-4',
        optionalClasses,
        className
      )}
      aria-disabled={disabled}
      {...props}
    />
  );
});

TextArea.displayName = 'TextArea';

export default TextArea;